import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ATID } from "@/lib/adminTestIds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  BarChart3, Newspaper, FileText, Clock, CalendarClock, Star, Zap,
  Eye, Users as UsersIcon, Download, Loader2, MapPin, RefreshCcw, Megaphone,
  Monitor, Tablet, Smartphone, TrendingUp,
} from "lucide-react";
import { formatDateShort } from "@/lib/date";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  BarChart, Bar, Legend,
} from "recharts";

const PRESETS = [
  { key: "today", label: "Hoy" },
  { key: "7d", label: "Últimos 7 días" },
  { key: "30d", label: "Últimos 30 días" },
  { key: "month", label: "Mes actual" },
  { key: "year", label: "Año actual" },
  { key: "custom", label: "Personalizado" },
];

const CHART_COLORS = ["#0d3b66", "#3a86ff", "#06a77d", "#f4a261", "#e76f51", "#7f5539", "#5f0f40"];

function StatCard({ testId, label, value, icon: Icon, accent }) {
  return (
    <div data-testid={testId} className="bg-white border border-softblue p-4 flex flex-col gap-1">
      <div className="flex items-center gap-2 text-textgray">
        <Icon className={`w-4 h-4 ${accent || "text-petroleum"}`} />
        <span className="text-[10px] uppercase tracking-widest font-semibold">{label}</span>
      </div>
      <div className="font-serif text-3xl font-black text-blueblack">{Number(value || 0).toLocaleString("es-AR")}</div>
    </div>
  );
}

function SectionCard({ title, subtitle, children, actions, testId }) {
  return (
    <section data-testid={testId} className="bg-white border border-softblue">
      <header className="flex items-center justify-between px-5 py-4 border-b border-softblue">
        <div>
          <h2 className="font-serif text-lg font-bold text-blueblack">{title}</h2>
          {subtitle && <p className="text-xs text-textgray mt-0.5">{subtitle}</p>}
        </div>
        {actions}
      </header>
      <div className="p-5">{children}</div>
    </section>
  );
}

function EmptyHint({ children }) {
  return <p className="text-sm text-textgray italic">{children}</p>;
}

function toLocalDateInput(d) {
  const dt = d instanceof Date ? d : new Date(d);
  const yyyy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const dd = String(dt.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

export default function AdminStatsPage() {
  const today = useMemo(() => toLocalDateInput(new Date()), []);
  const monthAgo = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return toLocalDateInput(d);
  }, []);

  const [filters, setFilters] = useState({
    preset: "30d",
    date_from: monthAgo,
    date_to: today,
    author_id: "",
    category_id: "",
    city: "",
  });

  const [availableFilters, setAvailableFilters] = useState({ authors: [], categories: [], cities: [] });
  const [data, setData] = useState(null);
  const [adsData, setAdsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    api.adminStatsFilters().then(setAvailableFilters).catch(() => {});
  }, []);

  const buildParams = (override) => {
    const f = { ...filters, ...(override || {}) };
    const params = { preset: f.preset };
    if (f.preset === "custom") {
      params.date_from = f.date_from;
      params.date_to = f.date_to;
    }
    if (f.author_id) params.author_id = f.author_id;
    if (f.category_id) params.category_id = f.category_id;
    if (f.city) params.city = f.city;
    return params;
  };

  const load = async (override) => {
    setLoading(true);
    try {
      const params = buildParams(override);
      const [editorial, ads] = await Promise.all([
        api.adminEditorialStats(params),
        api.adminAdsStats({ days: params.preset === "today" ? 1 : params.preset === "7d" ? 7 : 30 }).catch(() => null),
      ]);
      setData(editorial);
      setAdsData(ads);
    } catch (e) {
      toast.error("No se pudieron cargar las estadísticas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const applyPreset = (key) => {
    setFilters((f) => ({ ...f, preset: key }));
    load({ preset: key });
  };

  const applyFilters = () => load();

  const resetFilters = () => {
    setFilters({ preset: "30d", date_from: monthAgo, date_to: today, author_id: "", category_id: "", city: "" });
    load({ preset: "30d", author_id: "", category_id: "", city: "" });
  };

  const exportCsv = async () => {
    setDownloading(true);
    try {
      const blob = await api.adminEditorialStatsCsv(buildParams());
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `estadisticas-${filters.preset}-${today}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Exportación descargada");
    } catch (e) {
      toast.error("No se pudo exportar");
    } finally {
      setDownloading(false);
    }
  };

  const s = data?.summary || {};
  const daily = data?.daily || [];
  const byDevice = data?.by_device || [];
  const byCategory = data?.by_category || [];
  const byCity = data?.by_city || [];
  const topArticles = data?.top_articles || [];
  const byAuthor = data?.by_author || [];
  const featured = data?.featured_list || [];
  const breaking = data?.breaking_list || [];

  const totalViews = s.views_total || 0;
  const hasViews = totalViews > 0;

  const deviceIcon = (d) => {
    if (d === "mobile") return Smartphone;
    if (d === "tablet") return Tablet;
    return Monitor;
  };

  const adTotals = adsData?.totals || { impressions: 0, clicks: 0, ctr: 0 };

  return (
    <div data-testid={ATID.statsPage} className="max-w-7xl">
      <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-textgray">Panel</div>
          <h1 className="text-3xl font-black text-nightblue mt-1 font-serif flex items-center gap-2">
            <BarChart3 className="w-6 h-6" /> Estadísticas editoriales
          </h1>
          <p className="text-sm text-textgray mt-1">
            Rendimiento de contenido, audiencia y campañas publicitarias.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => load()} disabled={loading} data-testid="admin-stats-refresh">
            <RefreshCcw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Actualizar
          </Button>
          <Button onClick={exportCsv} disabled={downloading} data-testid={ATID.statsExportCsv}>
            {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            Exportar CSV
          </Button>
        </div>
      </header>

      {/* Preset chips */}
      <div className="flex flex-wrap gap-2 mb-4">
        {PRESETS.map((p) => (
          <button
            key={p.key}
            type="button"
            data-testid={ATID.statsPreset(p.key)}
            onClick={() => applyPreset(p.key)}
            className={`px-3 py-1.5 text-xs uppercase tracking-widest font-semibold border transition-colors ${
              filters.preset === p.key
                ? "bg-nightblue text-white border-nightblue"
                : "border-softblue text-nightblue hover:bg-lightgray"
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white border border-softblue p-4 mb-6 grid grid-cols-1 md:grid-cols-6 gap-3">
        {filters.preset === "custom" && (
          <>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-textgray mb-1 block">Desde</Label>
              <Input
                data-testid={ATID.statsDateFrom}
                type="date"
                value={filters.date_from}
                onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))}
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-textgray mb-1 block">Hasta</Label>
              <Input
                data-testid={ATID.statsDateTo}
                type="date"
                value={filters.date_to}
                onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))}
              />
            </div>
          </>
        )}
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-textgray mb-1 block">Autor</Label>
          <Select
            value={filters.author_id || "all"}
            onValueChange={(v) => setFilters((f) => ({ ...f, author_id: v === "all" ? "" : v }))}
          >
            <SelectTrigger data-testid={ATID.statsAuthor}><SelectValue placeholder="Todos" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los autores</SelectItem>
              {availableFilters.authors.map((a) => (
                <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-textgray mb-1 block">Categoría</Label>
          <Select
            value={filters.category_id || "all"}
            onValueChange={(v) => setFilters((f) => ({ ...f, category_id: v === "all" ? "" : v }))}
          >
            <SelectTrigger data-testid={ATID.statsCategory}><SelectValue placeholder="Todas" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las categorías</SelectItem>
              {availableFilters.categories.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-textgray mb-1 block">Ciudad</Label>
          <Select
            value={filters.city || "all"}
            onValueChange={(v) => setFilters((f) => ({ ...f, city: v === "all" ? "" : v }))}
          >
            <SelectTrigger data-testid={ATID.statsCity}><SelectValue placeholder="Todas" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las ciudades</SelectItem>
              {availableFilters.cities.map((c) => (
                <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-end gap-2">
          <Button data-testid={ATID.statsApply} onClick={applyFilters} className="w-full">Aplicar</Button>
          <Button data-testid={ATID.statsReset} variant="outline" onClick={resetFilters}>Limpiar</Button>
        </div>
      </div>

      {/* Summary counters */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
        <StatCard testId={ATID.statsCard("published_in_range")} label="Publicadas (rango)" value={s.published_in_range} icon={Newspaper} accent="text-petroleum" />
        <StatCard testId={ATID.statsCard("created_in_range")} label="Creadas (rango)" value={s.created_in_range} icon={FileText} accent="text-nightblue" />
        <StatCard testId={ATID.statsCard("drafts")} label="Borradores" value={s.drafts} icon={FileText} accent="text-textgray" />
        <StatCard testId={ATID.statsCard("pending")} label="Pendientes" value={s.pending} icon={Clock} accent="text-yellow-700" />
        <StatCard testId={ATID.statsCard("scheduled")} label="Programadas" value={s.scheduled} icon={CalendarClock} accent="text-turquoise" />
        <StatCard testId={ATID.statsCard("published_total")} label="Publicadas total" value={s.published_total} icon={Newspaper} accent="text-watergreen" />
        <StatCard testId={ATID.statsCard("featured")} label="Destacadas" value={s.featured} icon={Star} accent="text-yellow-600" />
        <StatCard testId={ATID.statsCard("breaking")} label="Último momento" value={s.breaking} icon={Zap} accent="text-red-600" />
        <StatCard testId={ATID.statsCard("views_total")} label="Visitas totales" value={s.views_total} icon={Eye} accent="text-nightblue" />
        <StatCard testId={ATID.statsCard("views_unique")} label="Sesiones únicas" value={s.views_unique_sessions} icon={UsersIcon} accent="text-petroleum" />
        <StatCard testId={ATID.statsCard("ads_impressions")} label="Impresiones (30d)" value={adTotals.impressions} icon={Eye} accent="text-turquoise" />
        <StatCard testId={ATID.statsCard("ads_clicks")} label="Clics publicitarios" value={adTotals.clicks} icon={TrendingUp} accent="text-watergreen" />
      </div>

      {/* Charts row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <SectionCard testId={ATID.statsChartDaily} title="Visitas por día" subtitle="Serie temporal según el rango seleccionado" >
          <div style={{ width: "100%", height: 260 }}>
            {hasViews ? (
              <ResponsiveContainer>
                <LineChart data={daily} margin={{ top: 8, right: 12, left: -12, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="day" tick={{ fontSize: 10, fill: "#64748b" }} tickFormatter={(v) => v.slice(5)} />
                  <YAxis tick={{ fontSize: 10, fill: "#64748b" }} allowDecimals={false} />
                  <Tooltip contentStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="views" stroke="#0d3b66" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center">
                <EmptyHint>Aún no hay visitas registradas en este rango. Las noticias comenzarán a acumular datos reales a partir de ahora.</EmptyHint>
              </div>
            )}
          </div>
        </SectionCard>

        <SectionCard testId={ATID.statsChartDevice} title="Visitas por dispositivo">
          <div style={{ width: "100%", height: 260 }}>
            {hasViews ? (
              <ResponsiveContainer>
                <BarChart data={byDevice} margin={{ top: 8, right: 12, left: -12, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="device" tick={{ fontSize: 11, fill: "#64748b" }} />
                  <YAxis tick={{ fontSize: 10, fill: "#64748b" }} allowDecimals={false} />
                  <Tooltip contentStyle={{ fontSize: 12 }} />
                  <Bar dataKey="views" fill="#06a77d" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center">
                <EmptyHint>Sin datos de dispositivos aún.</EmptyHint>
              </div>
            )}
          </div>
          {/* device summary chips */}
          <div className="flex justify-center gap-4 mt-3 text-xs text-textgray">
            {byDevice.map((d) => {
              const Ic = deviceIcon(d.device);
              return (
                <div key={d.device} className="flex items-center gap-1.5">
                  <Ic className="w-3.5 h-3.5" />
                  <span className="capitalize">{d.device}</span>
                  <span className="font-semibold text-nightblue">{d.views.toLocaleString("es-AR")}</span>
                </div>
              );
            })}
          </div>
        </SectionCard>

        <SectionCard testId={ATID.statsChartCity} title="Visitas por ciudad">
          <div style={{ width: "100%", height: 260 }}>
            {hasViews ? (
              <ResponsiveContainer>
                <BarChart data={byCity} margin={{ top: 8, right: 12, left: -12, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="city_name" tick={{ fontSize: 11, fill: "#64748b" }} />
                  <YAxis tick={{ fontSize: 10, fill: "#64748b" }} allowDecimals={false} />
                  <Tooltip contentStyle={{ fontSize: 12 }} />
                  <Bar dataKey="views" fill="#3a86ff" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center">
                <EmptyHint>Sin datos por ciudad aún.</EmptyHint>
              </div>
            )}
          </div>
        </SectionCard>
      </div>

      {/* Charts row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <SectionCard testId={ATID.statsChartCategory} title="Categorías más visitadas">
          <div style={{ width: "100%", height: 300 }}>
            {byCategory.length > 0 ? (
              <ResponsiveContainer>
                <BarChart data={byCategory.slice(0, 10)} layout="vertical" margin={{ top: 8, right: 12, left: 60, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 10, fill: "#64748b" }} allowDecimals={false} />
                  <YAxis type="category" dataKey="category_name" tick={{ fontSize: 11, fill: "#334155" }} width={120} />
                  <Tooltip contentStyle={{ fontSize: 12 }} />
                  <Bar dataKey="views" fill="#0d3b66" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center">
                <EmptyHint>Sin visitas por categoría aún.</EmptyHint>
              </div>
            )}
          </div>
        </SectionCard>

        <SectionCard testId={ATID.statsTopArticles} title="Noticias más vistas" subtitle="Top 10 por visitas en el rango">
          {topArticles.length > 0 ? (
            <ul className="divide-y divide-softblue text-sm">
              {topArticles.slice(0, 10).map((a, idx) => (
                <li key={a.id} className="py-2.5 flex items-start gap-3">
                  <span className="font-serif font-black text-2xl text-textgray/60 w-8 text-right">{idx + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-blueblack line-clamp-1">{a.title}</div>
                    <div className="text-xs text-textgray">{a.category_name} · {a.author_name}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-serif font-bold text-nightblue">{a.views.toLocaleString("es-AR")}</div>
                    <div className="text-[10px] uppercase tracking-widest text-textgray">visitas</div>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyHint>Sin datos de noticias vistas en este rango.</EmptyHint>
          )}
        </SectionCard>
      </div>

      {/* Publications by author + Featured + Breaking */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <SectionCard testId={ATID.statsByAuthor} title="Publicaciones por autor" subtitle="Rango de fecha aplicado">
          {byAuthor.length > 0 ? (
            <ul className="divide-y divide-softblue text-sm">
              {byAuthor.map((r) => (
                <li key={r.author_id || r.author_name} className="py-2 flex items-center justify-between">
                  <span className="text-blueblack">{r.author_name}</span>
                  <span className="font-semibold text-nightblue">{r.published}</span>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyHint>Sin publicaciones en este rango.</EmptyHint>
          )}
        </SectionCard>

        <SectionCard testId={ATID.statsFeaturedList} title="Noticias destacadas" subtitle="Publicadas y marcadas como destacadas">
          {featured.length > 0 ? (
            <ul className="divide-y divide-softblue text-sm">
              {featured.map((a) => (
                <li key={a.id} className="py-2">
                  <div className="font-medium text-blueblack line-clamp-1">{a.title}</div>
                  <div className="text-xs text-textgray flex items-center gap-2 mt-0.5">
                    <span>{a.main_category_name}</span>
                    <span>·</span>
                    <span>{a.author_name}</span>
                    {a.published_at && <><span>·</span><span>{formatDateShort(a.published_at)}</span></>}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyHint>No hay destacadas actualmente.</EmptyHint>
          )}
        </SectionCard>

        <SectionCard testId={ATID.statsBreakingList} title="Último momento" subtitle="Marcadas como noticia de último momento">
          {breaking.length > 0 ? (
            <ul className="divide-y divide-softblue text-sm">
              {breaking.map((a) => (
                <li key={a.id} className="py-2">
                  <div className="font-medium text-blueblack line-clamp-1">{a.title}</div>
                  <div className="text-xs text-textgray flex items-center gap-2 mt-0.5">
                    <span>{a.main_category_name}</span>
                    <span>·</span>
                    <span>{a.author_name}</span>
                    {a.published_at && <><span>·</span><span>{formatDateShort(a.published_at)}</span></>}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyHint>No hay noticias de último momento.</EmptyHint>
          )}
        </SectionCard>
      </div>

      {/* Ads */}
      <SectionCard
        testId={ATID.statsAdsSection}
        title={<span className="flex items-center gap-2"><Megaphone className="w-4 h-4" />Estadísticas publicitarias</span>}
        subtitle="Últimos 30 días — actualizado con los eventos reales de impresiones y clics"
      >
        {adsData ? (
          <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatCard
                testId={ATID.statsCard("ads_active")}
                label="Campañas activas"
                value={(adsData.by_campaign || []).filter((c) => c.impressions > 0 || c.clicks > 0).length}
                icon={Megaphone}
                accent="text-nightblue"
              />
              <StatCard testId={ATID.statsCard("ads_impressions_detail")} label="Impresiones" value={adTotals.impressions} icon={Eye} accent="text-turquoise" />
              <StatCard testId={ATID.statsCard("ads_clicks_detail")} label="Clics" value={adTotals.clicks} icon={TrendingUp} accent="text-watergreen" />
              <StatCard testId={ATID.statsCard("ads_ctr")} label="CTR" value={`${(adTotals.ctr || 0).toFixed(2)}%`} icon={BarChart3} accent="text-petroleum" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-xs uppercase tracking-widest text-textgray font-semibold mb-2">Rendimiento por ubicación</h3>
                {(adsData.by_slot || []).length > 0 ? (
                  <div style={{ width: "100%", height: 220 }}>
                    <ResponsiveContainer>
                      <BarChart data={adsData.by_slot.slice(0, 8)} margin={{ top: 8, right: 12, left: -12, bottom: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#64748b" }} interval={0} angle={-15} textAnchor="end" height={50} />
                        <YAxis tick={{ fontSize: 10, fill: "#64748b" }} allowDecimals={false} />
                        <Tooltip contentStyle={{ fontSize: 12 }} />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        <Bar dataKey="impressions" fill="#0d3b66" name="Impresiones" />
                        <Bar dataKey="clicks" fill="#06a77d" name="Clics" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <EmptyHint>Sin datos de ubicaciones.</EmptyHint>
                )}
              </div>

              <div>
                <h3 className="text-xs uppercase tracking-widest text-textgray font-semibold mb-2">Serie diaria (impresiones vs. clics)</h3>
                {(adsData.daily || []).length > 0 ? (
                  <div style={{ width: "100%", height: 220 }}>
                    <ResponsiveContainer>
                      <LineChart data={adsData.daily} margin={{ top: 8, right: 12, left: -12, bottom: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="day" tick={{ fontSize: 10, fill: "#64748b" }} tickFormatter={(v) => v.slice(5)} />
                        <YAxis tick={{ fontSize: 10, fill: "#64748b" }} allowDecimals={false} />
                        <Tooltip contentStyle={{ fontSize: 12 }} />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        <Line type="monotone" dataKey="impressions" stroke="#0d3b66" strokeWidth={2} dot={false} name="Impresiones" />
                        <Line type="monotone" dataKey="clicks" stroke="#06a77d" strokeWidth={2} dot={false} name="Clics" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <EmptyHint>Sin actividad publicitaria en el período.</EmptyHint>
                )}
              </div>
            </div>
          </div>
        ) : (
          <EmptyHint>Sin datos publicitarios disponibles.</EmptyHint>
        )}
      </SectionCard>
    </div>
  );
}
